import { Button } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { APP_SETUP_PAGE } from 'src/constants';
import { RouteContext, useFeatureFlags } from 'src/context';
import { AppSettingsModalContext } from 'src/features/app-settings-modal/AppSettingsModalContext';
import history from 'src/history';
import { useEditingApp } from 'src/legacy/components/Settings/AppSetupPage/SetupApp/useEditingApp';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { GraySmall, NonHoverBorder, red } from 'src/theme/colors';

import { useRemoveAppAction } from './useRemoveAppAction';

const useStyles = makeStyles()((theme) => ({
  deleteButton: {
    marginTop: theme.spacing(3),
    backgroundColor: red,
    borderColor: red,
    '&:hover': {
      backgroundColor: red,
    },
  },
  disabledButton: {
    marginTop: theme.spacing(3),
    backgroundColor: 'transparent',
    borderColor: NonHoverBorder,
    color: red,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  removeAppContent: {
    color: GraySmall,
    paddingTop: theme.spacing(0.5),
  },
}));

interface removeAppQuery {
  moduleId?: string;
  appId?: string;
}

/**
 * @description - Responsible for rendering the remove app button and the modal.
 * For core apps (Forms, Helpdesk, Billing etc), we only disable the app.
 * For other apps, we delete the app.
 * Additionally, we do not render this component when we're in add mode.
 */
export const RemoveApp = () => {
  const { query } = React.useContext(RouteContext);
  const modalCtx = React.useContext(AppSettingsModalContext);
  const { EnableConsistentAppSettingsLink } = useFeatureFlags();
  const appModule = useEditingApp();
  const { t } = useTranslation();
  const isAddMode = 'preset' in query;
  const { data: { moduleSettings = [], extensionsSettings } = {} } =
    useGetInstallsQuery('withFolders');
  const { classes } = useStyles();
  const moduleConfig =
    'moduleId' in query &&
    moduleSettings.find(
      (module) => module.id === (query as removeAppQuery).moduleId,
    );
  const isMarketplaceApp = !!(
    moduleConfig && extensionsSettings?.[moduleConfig.id]?.appId
  );
  const appId: string =
    'appId' in query ? ((query as removeAppQuery).appId as string) : '';

  const { openRemoveAppModal, modal, isCoreApp } = useRemoveAppAction({
    appModule,
    appId,
    onComplete: () => {
      if (modalCtx && EnableConsistentAppSettingsLink) {
        modalCtx.onClose();
      } else {
        history.push(APP_SETUP_PAGE.path);
      }
    },
  });

  if (isAddMode) return null;

  return (
    <div>
      {modal}
      {!appModule?.disabled && (
        <div>
          <BaseTypography fontType="18Medium">
            {t(($) =>
              isCoreApp
                ? $.page_apps.remove_app_modal.disable.title
                : $.page_apps.remove_app_modal.remove.title,
            )}
          </BaseTypography>
          {!isMarketplaceApp && (
            <BaseTypography
              fontType="13Regular"
              className={classes.removeAppContent}
            >
              {t(($) =>
                isCoreApp
                  ? $.page_apps.remove_app_modal.disable.description
                  : $.page_apps.remove_app_modal.remove.description,
              )}
            </BaseTypography>
          )}
          <Button
            onClick={openRemoveAppModal}
            variant="contained"
            color="primary"
            className={
              isCoreApp ? classes.disabledButton : classes.deleteButton
            }
          >
            {t(($) => (isCoreApp ? $.common.disable : $.common.delete))}
          </Button>
        </div>
      )}
    </div>
  );
};
